import {
    errorTag,
    PAGE_PRODUCT,
    recordCreateNewClient,
    recordError,
    recordEvent,
    recordEventCustomOnly,
    recordPage,
    recordRAPage,
} from 'utilities/uTagging';
import { PATH_PROFESSION } from 'constants/uTagConstants/paths';
import cryptoJS from 'crypto-js';
import { ONBOARDING_SUCCESS, PRODUCT_NAME_ROBO } from 'constants/uTagConstants/RAConstants';
import {
    ERROR_AL_VERIFICAR_FIOC,
    ERROR_CASO_CUENTA_AJENA,
    ERROR_CASO_CUENTA_PROPIA,
    ERROR_CASO_DESEMPLEADO,
    ERROR_CASO_EMPLEADO,
    ERROR_CASO_PENSIONISTA,
} from 'constants/analyticsErrors';
import UtagHelper from 'utilities/utagHelper';

const buildTagging = ({ userProfile, products }) => {
    const { userId, recordId, investmentType: userInvestmentType, docNumber } = userProfile;
    const investmentType = userInvestmentType ? userInvestmentType : '';
    const JointHolderInfo = UtagHelper.getJointHolderInfoFromUserProfile(userProfile);

    const docNumberEncrypted = docNumber ? cryptoJS.SHA256(docNumber).toString() : '';
    const doTagChangeFieldWorkBy = value => {
        recordEventCustomOnly({
            action: 'select',
            format: 'checkBox',
            component: 'form',
            element: `situation/check${value}`,
            eventCategory: PATH_PROFESSION,
            eventAction: 'selectCheckBox',
            eventLabel: `situation/check${value}`,
        });
    };

    const doTagSubmitWithoutActivity = ({ isTssChecked, profession }) => {
        const professionMapper = {
            X200A: '1',
            X300A: '2',
            X300B: '3',
        };

        recordEventCustomOnly({
            action: 'click',
            format: 'button',
            component: 'form',
            element: 'continue',
            data: `consulta TGSS:${isTssChecked ? 'checked' : 'unchecked'},situation5,dedication${
                professionMapper[profession]
            }`,
            accionEvento: 'clic',
            etiquetaEvento: 'continuar',
        });
    };

    const doTagSubmit = (accountType, values) => {
        const mapper = {
            '05': doTagSubmitWithoutActivity,
        };

        mapper[accountType]?.(values);
    };

    const doTagCancel = () => {
        recordEventCustomOnly({
            categoriaEvento: 'eventos onboarding',
            accionEvento: 'clic',
            etiquetaEvento: 'salir',
        });
    };

    const doTagValidateFields = () => {
        recordEventCustomOnly({
            categoriaEvento: 'eventos onboarding',
            accionEvento: 'clic',
            etiquetaEvento: 'continuar',
            event_name: 'button_click',
            action: 'click',
            format: 'button',
            component: 'form',
            element: 'continue',
        });
    };

    const doTagErrorValidateFields = ({ fieldsWithError, investmentType }) => {
        recordError(`[${fieldsWithError}]:Campos requeridos para continuar.`, investmentType);
        recordEvent(
            PATH_PROFESSION,
            PAGE_PRODUCT,
            'tu actividad',
            'tu actividad incorrecto',
            recordId,
            userId,
            '',
            investmentType,
            docNumberEncrypted
        );
    };

    const doTagCreateClient = userId => {
        recordEvent(
            PATH_PROFESSION,
            PAGE_PRODUCT,
            'tu actividad',
            'tu actividad correcto',
            userProfile.recordId,
            userId,
            '',
            investmentType,
            docNumberEncrypted,
            {
                categoriaEvento: ONBOARDING_SUCCESS,
                accionEvento: '/onboarding/tu-actividad/',
                etiquetaEvento: 'tu actividad',
            },
            {
                event_name: 'event_success',
                action: 'success',
                format: 'steps',
                component: 'funnel',
                element: 'activity',
            }
        );
        recordCreateNewClient(
            userProfile.recordId,
            userId,
            userProfile.product,
            userProfile.promoCode ? userProfile.promoCode : '',
            '',
            '',
            investmentType,
            JointHolderInfo
        );
    };

    const doTagErrorSaveFiocData = ({ data, statusCode }, kindAccount) => {
        errorTag(data, PATH_PROFESSION, kindAccount, statusCode);
    };

    const doTagRecordPage = () => {
        recordPage(
            PATH_PROFESSION,
            userProfile.recordId,
            userProfile.userId,
            userProfile.product,
            products.get('productsDescription'),
            userProfile.promoCode ? userProfile.promoCode : '',
            JointHolderInfo,
            '',
            '',
            userProfile.investmentType,
            {
                identificadorOnboarding: docNumberEncrypted,
                event_name: 'step_activity',
                pageName: '/onboarding/step4/activity',
                sectionFamily: 'banking',
                sectionCategory: 'onboarding',
                sectionName: 'userOnboarding',
                interactionType: 'funnel',
                serviceType: 'onboarding',
                processType: 'onboarding',
                processStep: 'step4',
                processDetail: 'activity',
                onboardingType: userProfile.investmentType,
            }
        );
    };

    const doTagRecordRAPage = ({ investProfile, objective }) => {
        recordRAPage(
            PATH_PROFESSION,
            `${PRODUCT_NAME_ROBO} ${investProfile} ${objective}`,
            userProfile.recordId,
            userProfile.userId,
            userProfile.product,
            products.get('productsDescription'),
            userProfile.promoCode ? userProfile.promoCode : '',
            false,
            '',
            '',
            userProfile.investmentType
        );
    };

    return {
        doTagChangeFieldWorkBy,
        doTagSubmit,
        doTagCancel,
        doTagValidateFields,
        doTagErrorValidateFields,
        doTagCreateClient,
        doTagErrorSaveSelfEmployed: error =>
            doTagErrorSaveFiocData(
                {
                    data: error.body,
                    statusCode: error.statusCode,
                },
                ERROR_CASO_CUENTA_PROPIA
            ),
        doTagErrorSaveEmployed: error =>
            doTagErrorSaveFiocData({ data: error.body, statusCode: error.statusCode }, ERROR_CASO_EMPLEADO),
        doTagErrorSaveRentier: error =>
            doTagErrorSaveFiocData(
                {
                    data: error.body,
                    statusCode: error.statusCode,
                },
                ERROR_CASO_CUENTA_AJENA
            ),
        doTagErrorSavePensioner: error =>
            doTagErrorSaveFiocData(
                {
                    data: error.body,
                    statusCode: error.statusCode,
                },
                ERROR_CASO_PENSIONISTA
            ),
        doTagErrorSaveWithoutActivity: error =>
            doTagErrorSaveFiocData(
                {
                    data: error.body,
                    statusCode: error.statusCode,
                },
                ERROR_CASO_DESEMPLEADO
            ),
        doTagErrorSaveFiocVerify: data => doTagErrorSaveFiocData({ data, statusCode: '' }, ERROR_AL_VERIFICAR_FIOC),
        doTagRecordPage,
        doTagRecordRAPage,
    };
};

export default buildTagging;
